// New/Rename/Duplicate/Delete tab modal for the exotics pricer tab system.
// Mirrors the OTC trader HandleTabModal but is decoupled from
// OTCTraderContext — tab uniqueness is checked against the exotics tabs
// list, token pair is limited to BTC/ETH (the existing Token type).
import { FC, useContext, useMemo, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import {
  ExoticsTabsContext,
  labelFromTabKey,
} from '../../providers/exotics-tabs/exotics-tabs-provider';
import { useAvailableTokens } from '../../providers/exotics-tokens/use-available-tokens';
import { Token } from './contracts';

export enum TabModalView {
  NEW_TAB,
  RENAME_TAB,
  DUPLICATE_TAB,
  DELETE_TAB,
}

const TITLES: Record<TabModalView, string> = {
  [TabModalView.NEW_TAB]: 'New tab',
  [TabModalView.RENAME_TAB]: 'Rename tab',
  [TabModalView.DUPLICATE_TAB]: 'Duplicate tab',
  [TabModalView.DELETE_TAB]: 'Delete tab?',
};

// Fallback while the registry call is in flight on first load.
const TOKEN_OPTIONS_FALLBACK: Token[] = ['BTC', 'ETH'];

interface Props {
  open: boolean;
  view: TabModalView;
  currentTabKey: string; // the tab the action targets (for rename/duplicate/delete)
  onClose: () => void;
}

const HandleExoticsTabModal: FC<Props> = ({ open, view, currentTabKey, onClose }) => {
  const { tabs, addTab, renameTab, duplicateTab, deleteTab } = useContext(ExoticsTabsContext);
  const { tokens: availableTokens } = useAvailableTokens();
  const tokenOptions: Token[] =
    availableTokens.length > 0 ? availableTokens.map((t) => t.baseQuote) : TOKEN_OPTIONS_FALLBACK;
  const quoteFor = (baseQuote: string): string => {
    const found = availableTokens.find((t) => t.baseQuote === baseQuote);
    return found?.quoteCcy ?? 'USDC';
  };
  const labelFor = (baseQuote: string): string => {
    const found = availableTokens.find((t) => t.baseQuote === baseQuote);
    return found?.baseCcy ?? baseQuote;
  };

  // Pre-fill the label with the current tab's name on RENAME / DUPLICATE.
  const initialLabel = useMemo(() => {
    const cur = tabs.find((t) => t.key === currentTabKey);
    return cur ? labelFromTabKey(cur.key, cur.label) : '';
  }, [tabs, currentTabKey]);

  const [label, setLabel] = useState('');
  const [token, setToken] = useState<Token>('BTC');

  // Reset form state each time the modal opens so a previous draft doesn't
  // leak into the next interaction.
  useEffect(() => {
    if (!open) return;
    setLabel(view === TabModalView.NEW_TAB ? '' : initialLabel);
    setToken('BTC');
  }, [open, view, initialLabel]);

  const labelExists = useMemo(
    () => tabs.some((t) => t.label.toLowerCase() === label.trim().toLowerCase()),
    [tabs, label],
  );

  const labelValid =
    label.trim().length > 0 && (view === TabModalView.RENAME_TAB ? true : !labelExists);

  const canSave =
    view === TabModalView.DELETE_TAB ||
    (view === TabModalView.NEW_TAB && labelValid) ||
    (view === TabModalView.RENAME_TAB && labelValid) ||
    (view === TabModalView.DUPLICATE_TAB && labelValid);

  const handleSave = () => {
    const trimmed = label.trim();
    switch (view) {
      case TabModalView.NEW_TAB:
        addTab(token, trimmed);
        break;
      case TabModalView.RENAME_TAB:
        renameTab(currentTabKey, trimmed);
        break;
      case TabModalView.DUPLICATE_TAB:
        duplicateTab(currentTabKey, trimmed);
        break;
      case TabModalView.DELETE_TAB:
        deleteTab(currentTabKey);
        break;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { backgroundColor: '#002649', backgroundImage: 'none', minWidth: 360 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1.25,
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.04em',
        }}
      >
        {TITLES[view]}
        <IconButton onClick={onClose} size="small" aria-label="close-tab-modal">
          <FontAwesomeIcon icon={faXmark} style={{ fontSize: 14 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        {view === TabModalView.DELETE_TAB ? (
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            This will permanently remove the tab and its instruments. Continue?
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {view === TabModalView.NEW_TAB && (
              <Box>
                <Typography
                  sx={{
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    mb: 0.5,
                  }}
                >
                  Token pair
                </Typography>
                <Select
                  size="small"
                  fullWidth
                  value={token}
                  onChange={(e) => setToken(e.target.value as Token)}
                  sx={{ fontSize: 12 }}
                  inputProps={{ 'aria-label': 'new-tab-token' }}
                >
                  {tokenOptions.map((t) => (
                    <MenuItem key={t} value={t} sx={{ fontSize: 12 }}>
                      {labelFor(t)} / {quoteFor(t)}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            <Box>
              <Typography
                sx={{
                  fontSize: 9,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  mb: 0.5,
                }}
              >
                Tab name
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                autoFocus
                placeholder="e.g. BTC straddle, ETH structures"
                inputProps={{ 'aria-label': 'tab-name', maxLength: 40 }}
                error={!!label.trim() && view !== TabModalView.RENAME_TAB && labelExists}
                helperText={
                  !!label.trim() && view !== TabModalView.RENAME_TAB && labelExists
                    ? 'A tab with this name already exists'
                    : ' '
                }
                sx={{ '& .MuiInputBase-input': { fontSize: 12 } }}
              />
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <Button onClick={onClose} size="small" sx={{ fontSize: 11, textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={!canSave}
          variant="contained"
          size="small"
          sx={{
            fontSize: 11,
            textTransform: 'none',
            backgroundColor: view === TabModalView.DELETE_TAB ? 'error.main' : '#003649',
            '&:hover': {
              backgroundColor: view === TabModalView.DELETE_TAB ? 'error.dark' : '#0a4960',
            },
          }}
        >
          {view === TabModalView.DELETE_TAB ? 'Delete' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HandleExoticsTabModal;
